import React, { useEffect, useRef, useState } from 'react';
import api from '../api/api';

function getAssetUrl(url) {
    if (!url) {
        return '';
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }

    const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api', '');
    if (url.startsWith('/')) {
        return `${apiBase}${url}`;
    }

    return `${apiBase}/${url}`;
}

function Users() {
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [expandedUserId, setExpandedUserId] = useState(null);
    const [addressesByUser, setAddressesByUser] = useState({});
    const [addressesLoadingUserId, setAddressesLoadingUserId] = useState(null);
    const [notificationTarget, setNotificationTarget] = useState(null);
    const [notificationForm, setNotificationForm] = useState({ title: '', body: '', imageUrl: '' });
    const [sendingNotification, setSendingNotification] = useState(false);
    const [uploadingNotificationImage, setUploadingNotificationImage] = useState(false);
    const notificationImageInputRef = useRef(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await api.get('/User');
            setUsers(res.data || []);
        } catch {
            alert('فشل تحميل المستخدمين');
        } finally {
            setLoading(false);
        }
    };

    const searchUsers = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/User/search?query=${encodeURIComponent(searchQuery)}`);
            setUsers(res.data || []);
        } catch {
            alert('فشل البحث عن المستخدمين');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            searchUsers();
        } else {
            fetchUsers();
        }
    };

    const handleBlockToggle = async (id) => {
        try {
            // First get the current user state to determine which endpoint to call
            const user = users.find(u => u.id === id);
            if (!user) return;
            
            if (user.isBlocked) {
                // User is currently blocked, so unblock them
                await api.put(`/User/unblock/${id}`);
            } else {
                // User is currently not blocked, so block them
                await api.put(`/User/block/${id}`);
            }
            
            fetchUsers();
        } catch {
            alert('فشل في تحديث حالة المستخدم');
        }
    };

    const handleDeleteUser = async (id, username) => {
        if (window.confirm(`هل أنت متأكد من حذف المستخدم "${username}"؟ لا يمكن التراجع عن هذا الإجراء.`)) {
            try {
                await api.delete(`/User/${id}`);
                alert('تم حذف المستخدم بنجاح');
                fetchUsers();
            } catch (error) {
                console.error('Delete error:', error);
                let errorMessage = 'فشل في حذف المستخدم';
                
                if (error.response?.status === 400) {
                    errorMessage = 'لا يمكن حذف المستخدم لأن لديه طلبات مرتبطة. يمكنك حظر المستخدم بدلاً من ذلك.';
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response?.status === 500) {
                    errorMessage = 'خطأ في الخادم. قد يكون للمستخدم بيانات مرتبطة تمنع الحذف.';
                }
                
                alert(errorMessage);
            }
        }
    };

    const handleToggleAddresses = async (user) => {
        if (expandedUserId === user.id) {
            setExpandedUserId(null);
            return;
        }

        setExpandedUserId(user.id);

        if (addressesByUser[user.id]) {
            return;
        }

        setAddressesLoadingUserId(user.id);
        try {
            const res = await api.get(`/User/${user.id}/addresses`);
            setAddressesByUser((prev) => ({
                ...prev,
                [user.id]: res.data,
            }));
        } catch {
            alert('فشل تحميل عناوين المستخدم');
        } finally {
            setAddressesLoadingUserId(null);
        }
    };

    const resetNotificationComposer = () => {
        setNotificationTarget(null);
        setNotificationForm({ title: '', body: '', imageUrl: '' });
        if (notificationImageInputRef.current) {
            notificationImageInputRef.current.value = '';
        }
    };

    const openNotificationComposerForUser = (user) => {
        const label = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
            || user.username
            || user.phoneNumber
            || `#${user.id}`;

        setNotificationTarget({
            mode: 'single',
            userId: user.id,
            label,
        });
        setNotificationForm({ title: '', body: '', imageUrl: '' });
        if (notificationImageInputRef.current) {
            notificationImageInputRef.current.value = '';
        }
    };

    const openNotificationComposerForAllUsers = () => {
        setNotificationTarget({
            mode: 'all',
            label: 'جميع المستخدمين',
        });
        setNotificationForm({ title: '', body: '', imageUrl: '' });
        if (notificationImageInputRef.current) {
            notificationImageInputRef.current.value = '';
        }
    };

    const handleNotificationFormChange = (field, value) => {
        setNotificationForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleNotificationImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }

        setUploadingNotificationImage(true);
        try {
            const formData = new FormData();
            formData.append('image', file);

            const response = await api.post('/Upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            const imageUrl = response.data?.imageUrl || '';
            setNotificationForm((prev) => ({
                ...prev,
                imageUrl,
            }));
        } catch (error) {
            console.error('Notification image upload error:', error);
            alert('فشل في رفع صورة الإشعار');
            if (notificationImageInputRef.current) {
                notificationImageInputRef.current.value = '';
            }
        } finally {
            setUploadingNotificationImage(false);
        }
    };

    const handleRemoveNotificationImage = () => {
        setNotificationForm((prev) => ({
            ...prev,
            imageUrl: '',
        }));
        if (notificationImageInputRef.current) {
            notificationImageInputRef.current.value = '';
        }
    };

    const handleSendNotification = async (e) => {
        e.preventDefault();

        if (!notificationTarget) {
            return;
        }

        const title = notificationForm.title.trim();
        const body = notificationForm.body.trim();

        if (!title || !body) {
            alert('يرجى إدخال عنوان الإشعار ونصه');
            return;
        }

        setSendingNotification(true);
        try {
            const response = await api.post('/notifications/admin/users/send', {
                title,
                body,
                imageUrl: notificationForm.imageUrl || undefined,
                sendToAllUsers: notificationTarget.mode === 'all',
                userIds: notificationTarget.mode === 'single' ? [notificationTarget.userId] : [],
            });

            const sentCount = Number(response.data?.sentCount || 0);
            if (sentCount > 0) {
                alert(
                    notificationTarget.mode === 'all'
                        ? `تم إرسال الإشعار إلى ${sentCount} مستخدم`
                        : 'تم إرسال الإشعار بنجاح'
                );
            } else {
                alert('لم يتم العثور على مستخدمين لإرسال الإشعار');
            }

            resetNotificationComposer();
        } catch (error) {
            console.error('Notification send error:', error);
            const errorMessage = error.response?.data?.message
                || error.response?.data?.title
                || 'فشل في إرسال الإشعار';
            alert(errorMessage);
        } finally {
            setSendingNotification(false);
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                <h4 className="mb-0">إدارة المستخدمين</h4>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={openNotificationComposerForAllUsers}
                >
                    إرسال إشعار لجميع المستخدمين
                </button>
            </div>
            
            {/* Search Bar */}
            <div className="mb-3">
                <form onSubmit={handleSearch} className="d-flex flex-wrap gap-2">
                    <input
                        type="text"
                        className="form-control me-2"
                        placeholder="البحث عن المستخدمين (الاسم، اسم المستخدم، رقم الهاتف)"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button type="submit" className="btn btn-primary me-2" disabled={loading}>
                        {loading ? 'جاري البحث...' : 'بحث'}
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-secondary" 
                        onClick={() => {
                            setSearchQuery('');
                            fetchUsers();
                        }}
                    >
                        إعادة تعيين
                    </button>
                </form>
            </div>

            {notificationTarget && (
                <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                            <div>
                                <h5 className="mb-1">إرسال إشعار</h5>
                                <div className="text-muted small">
                                    {notificationTarget.mode === 'all'
                                        ? 'سيتم إرسال هذا الإشعار إلى جميع المستخدمين المسجلين في التطبيق.'
                                        : `سيتم إرسال هذا الإشعار إلى المستخدم: ${notificationTarget.label}`}
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <span className={`badge ${notificationTarget.mode === 'all' ? 'bg-primary' : 'bg-info text-dark'}`}>
                                    {notificationTarget.mode === 'all' ? 'إرسال جماعي' : 'إرسال فردي'}
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={resetNotificationComposer}
                                    disabled={sendingNotification}
                                >
                                    إلغاء
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleSendNotification}>
                            <div className="mb-3">
                                <label className="form-label">عنوان الإشعار</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    maxLength={160}
                                    value={notificationForm.title}
                                    onChange={(e) => handleNotificationFormChange('title', e.target.value)}
                                    placeholder="مثال: عرض جديد أو تحديث مهم"
                                    disabled={sendingNotification}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">نص الإشعار</label>
                                <textarea
                                    className="form-control"
                                    rows="4"
                                    maxLength={1000}
                                    value={notificationForm.body}
                                    onChange={(e) => handleNotificationFormChange('body', e.target.value)}
                                    placeholder="اكتب الرسالة التي ستصل إلى المستخدمين"
                                    disabled={sendingNotification}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">صورة الإشعار داخل التطبيق</label>
                                <input
                                    ref={notificationImageInputRef}
                                    type="file"
                                    className="form-control"
                                    accept="image/*"
                                    onChange={handleNotificationImageUpload}
                                    disabled={sendingNotification || uploadingNotificationImage}
                                />
                                <div className="form-text">
                                    ستظهر هذه الصورة داخل صندوق الإشعارات في التطبيق، وليس في إشعار النظام.
                                </div>
                            </div>

                            {uploadingNotificationImage && (
                                <div className="text-muted small mb-3">جاري رفع الصورة...</div>
                            )}

                            {notificationForm.imageUrl && (
                                <div className="mb-3">
                                    <div className="border rounded p-2 bg-light">
                                        <img
                                            src={getAssetUrl(notificationForm.imageUrl)}
                                            alt="معاينة صورة الإشعار"
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: 220,
                                                borderRadius: 12,
                                                display: 'block',
                                                marginBottom: 12,
                                                objectFit: 'cover'
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={handleRemoveNotificationImage}
                                            disabled={sendingNotification || uploadingNotificationImage}
                                        >
                                            إزالة الصورة
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="d-flex justify-content-end">
                                <button
                                    type="submit"
                                    className="btn btn-success"
                                    disabled={sendingNotification || uploadingNotificationImage}
                                >
                                    {sendingNotification ? 'جاري الإرسال...' : 'إرسال الإشعار'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">جاري التحميل...</span>
                    </div>
                </div>
            ) : (
                <table className="table table-bordered table-striped">
                    <thead>
                        <tr>
                            <th>المعرف</th>
                            <th>اسم المستخدم</th>
                            <th>الاسم الأول</th>
                            <th>الاسم الأخير</th>
                            <th>رقم الهاتف</th>
                            <th>نقاط الولاء</th>
                            <th>الحالة</th>
                            <th>الخيارات</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => {
                            const addressData = addressesByUser[u.id];
                            const userAddresses = addressData?.addresses || [];
                            const isExpanded = expandedUserId === u.id;
                            const isAddressLoading = addressesLoadingUserId === u.id;

                            return (
                                <React.Fragment key={u.id}>
                                    <tr>
                                        <td>{u.id}</td>
                                        <td>{u.username || '—'}</td>
                                        <td>{u.firstName || '—'}</td>
                                        <td>{u.lastName || '—'}</td>
                                        <td>{u.phoneNumber || '—'}</td>
                                        <td>{u.loyaltyPoints || 0}</td>
                                        <td>
                                            <span className={`badge ${u.isBlocked ? 'bg-danger' : 'bg-success'}`}>
                                                {u.isBlocked ? 'محظور' : 'نشط'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-2">
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => handleToggleAddresses(u)}
                                                >
                                                    {isExpanded ? 'إخفاء العناوين' : 'عرض العناوين'}
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-success"
                                                    onClick={() => openNotificationComposerForUser(u)}
                                                >
                                                    إرسال إشعار
                                                </button>
                                                <button
                                                    className={`btn btn-sm ${u.isBlocked ? 'btn-success' : 'btn-warning'}`}
                                                    onClick={() => handleBlockToggle(u.id)}
                                                >
                                                    {u.isBlocked ? 'إلغاء الحظر' : 'حظر'}
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleDeleteUser(u.id, u.username || u.phoneNumber)}
                                                >
                                                    حذف
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr>
                                            <td colSpan="8" className="bg-light">
                                                <div className="p-3">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <strong>
                                                            عناوين المستخدم: {u.firstName || u.username || u.phoneNumber || `#${u.id}`}
                                                        </strong>
                                                        {!isAddressLoading && (
                                                            <span className="badge bg-secondary">
                                                                {userAddresses.length} عنوان
                                                            </span>
                                                        )}
                                                    </div>

                                                    {isAddressLoading ? (
                                                        <div className="text-center py-3">
                                                            <div className="spinner-border spinner-border-sm" role="status">
                                                                <span className="visually-hidden">جاري التحميل...</span>
                                                            </div>
                                                        </div>
                                                    ) : userAddresses.length === 0 ? (
                                                        <div className="text-muted">لا توجد عناوين محفوظة لهذا المستخدم</div>
                                                    ) : (
                                                        <div className="row g-3">
                                                            {userAddresses.map((address) => {
                                                                const addressDescription = address.description || address.notes;

                                                                return (
                                                                    <div className="col-md-6" key={address.id}>
                                                                        <div className="card h-100">
                                                                            <div className="card-body">
                                                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                                                    <strong>{address.label || 'بدون تسمية'}</strong>
                                                                                    {address.isDefault && (
                                                                                        <span className="badge bg-primary">افتراضي</span>
                                                                                    )}
                                                                                </div>
                                                                                <div className="mb-2">{address.addressText}</div>
                                                                                {(address.building || address.floor || address.apartment) && (
                                                                                    <div className="text-muted small mb-2">
                                                                                        {[address.building && `البناء: ${address.building}`, address.floor && `الطابق: ${address.floor}`, address.apartment && `الشقة: ${address.apartment}`]
                                                                                            .filter(Boolean)
                                                                                            .join(' | ')}
                                                                                    </div>
                                                                                )}
                                                                                {addressDescription && (
                                                                                    <div className="text-muted small mb-2">الوصف: {addressDescription}</div>
                                                                                )}
                                                                                {(address.latitude || address.longitude) && (
                                                                                    <div className="small mb-2">
                                                                                        الإحداثيات: {address.latitude || '—'}, {address.longitude || '—'}
                                                                                    </div>
                                                                                )}
                                                                                {address.mapUrl && (
                                                                                    <a href={address.mapUrl} target="_blank" rel="noreferrer">
                                                                                        فتح الموقع على الخريطة
                                                                                    </a>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {users.length === 0 && (
                            <tr>
                                <td colSpan="8" className="text-center">
                                    {searchQuery ? 'لم يتم العثور على مستخدمين' : 'لا يوجد مستخدمون'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default Users;
